/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Slide,
  Tab,
  Tabs,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
} from '@mui/material';
import { 
  ExpandMore, Add, Remove, Error,
  Article as ArticleIcon, Person,
  Warning, Search,
  School as SchoolIcon,
  Lock as LockIcon,
  Close,
  Info,
  CheckCircle,
  NavigateNext,
  Visibility,
  Edit,
  Description,
  Assignment,
  MenuBook,
  Science,
  Calculate,
  Psychology,
  FormatQuote,
  Summarize,
  AccountTree,
  LibraryBooks,
  CheckBox,
  Tune,
  NoteAdd
} from '@mui/icons-material';
import { getIdCategory, getIdFullName, getTodaysDate } from '../../utilities/Functions';
import GuestUser from '../main/GuestUser';
import NavigationMenu from '../navigation/NavigationMenu';
import { keyframes } from '@emotion/react';
import { AUTH_TOKEN, BASE_URL, headers } from '../../utilities/Env';
import { APIcategoriesTheme, APIExtracttText, APIResearchCheckSimilarities, APIResearcherList, APIResearchStore, APIResearchUpdate, APIUniversitiesList } from '../../utilities/APIS';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import { makeStyles } from '@mui/styles';
import LoadingState from '../../components/LoadingState';

const fadeIn = keyframes`
from { opacity: 0; transform: translateY(20px); }
to { opacity: 1; transform: translateY(0); }
`;

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: '8px 0',
  borderRadius: '8px',
  boxShadow: theme.shadows[1],
  '&:before': {
    display: 'none',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  borderRadius: '12px',
  boxShadow: theme.shadows[3],
  animation: `${fadeIn} 0.5s ease-out`,
}));

const CustomTab = styled(Tab)(({ theme, iscompleted, isdisabled }) => ({
  minHeight: '48px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '20',
  },
  '& .MuiTab-iconWrapper': {
    marginRight: theme.spacing(1),
  },
  ...(iscompleted === 'true' && {
    color: theme.palette.success.main,
    '& .MuiTab-iconWrapper': {
      color: theme.palette.success.main,
    },
  }),
  ...(isdisabled === 'true' && {
    color: theme.palette.text.disabled,
    '& .MuiTab-iconWrapper': {
      color: theme.palette.text.disabled,
    },
  }),
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.light,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main + '08',
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px 12px 0 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1)
}));

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  maxHeight: '500px',
  overflowY: 'auto',
}));

const WordCountChip = styled(Chip)(({ theme, isover }) => ({
  backgroundColor: isover === 'true' ? theme.palette.error.main : theme.palette.success.main,
  color: 'white',
  '&:hover': {
    backgroundColor: isover === 'true' ? theme.palette.error.dark : theme.palette.success.dark,
  }
}));

// PDF Viewer Component
const PDFViewer = ({ open, onClose, pdfUrl, title = "PDF Document" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '70vh' }}
            title={title}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Word limit configuration
const WORD_LIMITS = {
  researchTopic: { max: 20, required: true },
  abstract: { max: 300, required: true },
  acronyms: { max: 1000, required: false },
  definitionOfTerms: { max: 200, required: false },
  introduction: { max: 500, required: false },
  objectives: { max: 100, required: true },
  hypothesis: { max: 100, required: false },
  justification: { max: 1000, required: false, hiddenInView: true },
  significance: { max: 1000, required: false, hiddenInView: true },
  theoreticalFramework: { max: 500, required: false },
  literatureReview: { max: 2000, required: false },
  methodology: { max: 5000, required: true },
  resultsFindings: { max: 5000, required: true },
  discussionOfResults: { max: 2000, required: false },
  conclusionsRecommendations: { max: 1000, required: true },
  references: { max: 1000, required: true },
  appendices: { max: 1000, required: false }
};

const SubmitResearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [researchersData, setResearchersData] = useState([]);
  const [universitiesData, setUniversitiesData] = useState([]);
  const classes = useStyles();
  const [postStatus, setPostStatus] = useState('ADD');
  const [editingResearchId, setEditingResearchId] = useState(null);
  const showToast = useCustomToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [similarityResult, setSimilarityResult] = useState(null);
  const [checkingSimilarities, setCheckingSimilarities] = useState(false);
  const [pdfPlainText, setPdfPlainText] = useState('');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [extractingSections, setExtractingSections] = useState(false);
  const [previewPdfDialogOpen, setPreviewPdfDialogOpen] = useState(false);
  const [wordCounts, setWordCounts] = useState({});

  const similarityThreshold = 15;

  // Research data state with all new fields
  const [researchData, setResearchData] = useState({
    researchTopic: '',
    abstract: '',
    acronyms: '',
    definitionOfTerms: '',
    introduction: '',
    objectives: '',
    hypothesis: '',
    justification: '',
    significance: '',
    theoreticalFramework: '',
    literatureReview: '',
    methodology: '',
    resultsFindings: '',
    discussionOfResults: '',
    conclusionsRecommendations: '',
    references: '',
    appendices: ''
  });

  const [state, setState] = useState({
    delaySeconds: 2000,
    showSubmitBtn: true,
    showContinueToResearchBtn: false,
    showPostingAnimation: false,
    postErrorMsg: '',
    SimilaritiesErrorMsg: '',
    userLoggedIn: false,
    userName: '',
    userId: '',
    userUniversity: '',
    ThemesData: [],
    ResearchArea: '',
    file: null,
    pdfFilePreview: null,
    pdfFileName: null,
    ResearcherPdfFile: '',
    fileName: '',
    fileType: '',
    base64URL: "",
    ResearchStatusTextInput: '',
    ResearchProjectTextInput: '',
    completedValue: '100 %',
    stringsToCompare: [],
    pdfPlainTextError: '',
    pdfPlainTextSuccess: '',
    ResearchSimilarities: [],
    ResearchesToCompareTo: [],
    NoSimilaritiesInResults: '',
    NoSimilaritiesUploadedResearch: '',
    showResearchStatusTab: false,
    showSummaryPreviewTab: false,
    showSimilaritiesTab: false,
    showSubmitTab: false,
    activeTab: 'ResearchHit',
    requiredFieldsCompleted: {}
  });

  useEffect(() => {
    fetchData();
    loadUser();
    checkEditMode();
  }, []);

  // Check if we're in edit mode and load research data
  const checkEditMode = () => {
    if (location.state) {
      const researchDataFromState = location.state;
      setPostStatus('UPDATE');
      setPdfPlainText(researchDataFromState.pdfPlainText || '');
      
      if (researchDataFromState.id) {
        setEditingResearchId(researchDataFromState.id);
      }

      // Load all research fields
      setResearchData({
        researchTopic: researchDataFromState.researchTopic || '',
        abstract: researchDataFromState.abstract || '',
        acronyms: researchDataFromState.acronyms || '',
        definitionOfTerms: researchDataFromState.definition_of_terms || '',
        introduction: researchDataFromState.introduction || '',
        objectives: researchDataFromState.objectives || '',
        hypothesis: researchDataFromState.hypothesis || '',
        justification: researchDataFromState.justification || '',
        significance: researchDataFromState.significance || '',
        theoreticalFramework: researchDataFromState.theoretical_framework || '',
        literatureReview: researchDataFromState.literature_review || '',
        methodology: researchDataFromState.methodology || '',
        resultsFindings: researchDataFromState.results_findings || '',
        discussionOfResults: researchDataFromState.discussion_of_results || '',
        conclusionsRecommendations: researchDataFromState.conclusions_recommendations || '',
        references: researchDataFromState.references || '',
        appendices: researchDataFromState.appendices || ''
      });

      setState(prev => ({
        ...prev,
        ResearchArea: researchDataFromState.researchArea || '',
        ResearchStatusTextInput: researchDataFromState.researchStatus || '',
        ResearchProjectTextInput: researchDataFromState.researchType || '',
        fileName: researchDataFromState.fileName ? researchDataFromState.fileName.split('/').pop() : '',
        pdfFilePreview: researchDataFromState.fileName ? `${BASE_URL}${researchDataFromState.fileName}` : null,
        showResearchStatusTab: true,
        showSummaryPreviewTab: true,
        showSimilaritiesTab: true,
        showSubmitTab: true
      }));

      // Calculate word counts for edit mode
      calculateAllWordCounts(researchDataFromState);
      showToast("Edit Mode", "You are now editing an existing research", "info");
    }
  };

  const calculateWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const calculateAllWordCounts = (data) => {
    const counts = {
      researchTopic: calculateWordCount(data.researchTopic),
      abstract: calculateWordCount(data.abstract),
      acronyms: calculateWordCount(data.acronyms),
      definitionOfTerms: calculateWordCount(data.definition_of_terms),
      introduction: calculateWordCount(data.introduction),
      objectives: calculateWordCount(data.objectives),
      hypothesis: calculateWordCount(data.hypothesis),
      justification: calculateWordCount(data.justification),
      significance: calculateWordCount(data.significance),
      theoreticalFramework: calculateWordCount(data.theoretical_framework),
      literatureReview: calculateWordCount(data.literature_review),
      methodology: calculateWordCount(data.methodology),
      resultsFindings: calculateWordCount(data.results_findings),
      discussionOfResults: calculateWordCount(data.discussion_of_results),
      conclusionsRecommendations: calculateWordCount(data.conclusions_recommendations),
      references: calculateWordCount(data.references),
      appendices: calculateWordCount(data.appendices)
    };
    setWordCounts(counts);
  };

  const updateWordCount = (field, text) => {
    const count = calculateWordCount(text);
    setWordCounts(prev => ({ ...prev, [field]: count }));
    return count;
  };

  const isWordLimitValid = (field, text) => {
    const limit = WORD_LIMITS[field];
    if (!limit) return true;
    const count = calculateWordCount(text);
    return count <= limit.max;
  };

  const loadUser = () => {
    const user = sessionStorage.getItem('reseacher');
    if (user) {
      const jsonData = JSON.parse(user);
      setState(prev => ({
        ...prev,
        userName: jsonData.name,
        userLoggedIn: jsonData.loggedIn,
        userId: jsonData.id,
        userUniversity: jsonData.university
      }));
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [themesRes, unisRes, researchersRes] = await Promise.all([
        axios.get(APIcategoriesTheme, { headers }),
        axios.get(APIUniversitiesList, { headers }),
        axios.get(APIResearcherList, { headers }),
      ]);

      const uni = Array.isArray(unisRes.data.data) ? unisRes.data.data.reverse() : [];
      setUniversitiesData(uni);

      const researchers = Array.isArray(researchersRes.data.data) ? researchersRes.data.data.reverse() : [];
      setResearchersData(researchers);
      
      const data = Array.isArray(themesRes.data.data) ? themesRes.data.data.reverse() : [];
      setState(prev => ({
        ...prev,
        ThemesData: data,
      }));
    } catch (error) {
      let errorMessage = "Failed to load data";
      if (error.response) {
        errorMessage = error.response.data.message || error.message;
      } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // PDF processing
  const PdfTextExtractor = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      showToast("File Error", "Please upload a valid PDF file", "error");
      return;
    }

    if (file.type !== 'application/pdf') {
      showToast("File Type Error", "Please upload a PDF file only", "error");
      return;
    }

    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast("File Size Error", "File size too large. Please upload a PDF smaller than 15MB", "error");
      return;
    }

    setState(prev => ({
      ...prev,
      pdfPlainTextError: '',
      pdfPlainTextSuccess: ''
    }));

    setExtractingSections(true);

    try {
      showToast("Processing Started", "Starting PDF processing...", "info");
      const formData = new FormData();
      formData.append('pdf_file', file);

      formData.append('extraction_options', JSON.stringify({
        ignore_tables: true,
        ignore_images: true,
        ignore_forms: true,
        extract_only_text: true,
        remove_duplicate_spaces: true,
        preserve_layout: false
      }));

      const response = await fetch(APIExtracttText, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${AUTH_TOKEN}`
        }
      });

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to extract text from PDF');
      }

      if (!result.data || !result.data.text) {
        throw new Error('No text content received from server');
      }

      const extractedText = result.data.text;
      const stats = result.data.stats || { word_count: 0, page_count: 0, processing_time_ms: 0 };

      if (!extractedText || extractedText.trim().length === 0) {
        showToast("No Text Found", "No extractable text content found in the PDF.", "warning");
        return;
      }

      setPdfPlainText(extractedText);

      const successMessage = `Successfully extracted ${stats.word_count} words from ${stats.page_count} page(s).`;
      showToast("Extraction Successful", successMessage, "success");

      setState(prev => ({
        ...prev,
        pdfPlainTextSuccess: successMessage,
        pdfPlainTextError: ''
      }));

      setTimeout(() => {
        setState(prev => ({
          ...prev,
          showSummaryPreviewTab: true,
          showSimilaritiesTab: true,
          showSubmitTab: true
        }));
      }, 1000);

      setTimeout(() => {
        if (typeof handleSubmitSimilarity === 'function') {
          handleSubmitSimilarity(extractedText, editingResearchId);
        }
      }, 2000);

    } catch (err) {
      console.error('PDF processing error:', err);
      let errorTitle = 'Processing Error';
      let errorMessage = 'Error processing PDF file';

      if (err.message.includes('network') || err.message.includes('fetch')) {
        errorTitle = 'Network Error';
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else if (err.message.includes('password') || err.message.includes('encrypted')) {
        errorTitle = 'Password Protected';
        errorMessage = 'PDF is password protected. Please provide an unencrypted PDF.';
      } else {
        errorMessage = err.message || 'Unknown error occurred. Please try again.';
      }

      showToast(errorTitle, errorMessage, "error");
      setState(prev => ({
        ...prev,
        pdfPlainTextError: `${errorTitle}: ${errorMessage}`,
        pdfPlainTextSuccess: ''
      }));
    } finally {
      setExtractingSections(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    if (newValue === 'Attachments' && !state.showResearchStatusTab) {
      showToast("Action Required", "Please enter a Research Topic first", "warning");
      return;
    }
    if (newValue === 'Summary-Preview' && !state.showSummaryPreviewTab) {
      showToast("Action Required", "Please complete the Research Status tab first", "warning");
      return;
    }
    if (newValue === 'Similarities' && !state.showSimilaritiesTab) {
      showToast("Action Required", "Please complete the previous tabs first", "warning");
      return;
    }
    if (newValue === 'Submit' && !state.showSubmitTab) {
      showToast("Action Required", "Please complete all previous tabs first", "warning");
      return;
    }

    setState(prev => ({ ...prev, activeTab: newValue }));
  };

  const isTabCompleted = (tabName) => {
    switch (tabName) {
      case 'ResearchHit':
        return researchData.researchTopic.trim().length > 0 && 
               researchData.abstract.trim().length > 0;
      case 'Attachments':
        return state.showResearchStatusTab && 
               state.ResearchArea.trim().length > 0 &&
               state.ResearchStatusTextInput.trim().length > 0 &&
               state.ResearchProjectTextInput.trim().length > 0;
      case 'Summary-Preview':
        return state.showSummaryPreviewTab;
      case 'Similarities':
        return state.showSimilaritiesTab;
      case 'Submit':
        return state.showSubmitTab && 
               researchData.methodology.trim().length > 0 &&
               researchData.resultsFindings.trim().length > 0 &&
               researchData.conclusionsRecommendations.trim().length > 0 &&
               researchData.references.trim().length > 0;
      default:
        return false;
    }
  };

  const getTabIcon = (tabName) => {
    const isCompleted = isTabCompleted(tabName);
    const isDisabled = !getTabEnabled(tabName);
    const isActive = state.activeTab === tabName;

    if (isCompleted) {
      return <CheckCircle fontSize="small" />;
    } else if (isDisabled) {
      return <LockIcon fontSize="small" />;
    } else if (isActive) {
      return <NavigateNext fontSize="small" />;
    } else {
      return <Info fontSize="small" />;
    }
  };

  const getTabEnabled = (tabName) => {
    switch (tabName) {
      case 'ResearchHit':
        return true;
      case 'Attachments':
        return state.showResearchStatusTab;
      case 'Summary-Preview':
        return state.showSummaryPreviewTab;
      case 'Similarities':
        return state.showSimilaritiesTab;
      case 'Submit':
        return state.showSubmitTab;
      default:
        return false;
    }
  };

  const handleResearchFieldChange = (field, value) => {
    const wordCount = updateWordCount(field, value);
    setResearchData(prev => ({ ...prev, [field]: value }));
    
    // Check if the field is valid
    const isValid = wordCount <= WORD_LIMITS[field]?.max;
    
    if (!isValid && WORD_LIMITS[field]?.required) {
      showToast("Word Limit Exceeded", `${field} exceeds the maximum ${WORD_LIMITS[field].max} words limit`, "warning");
    }
    
    // Enable Research Status tab when research topic is typed (not empty)
    if (field === 'researchTopic' && value.trim().length > 0) {
      setState(prev => ({
        ...prev,
        showResearchStatusTab: true
      }));
      showToast("Success", "Research topic added! You can now proceed to Research Status tab.", "success");
    }
    
    // Check required fields for completion
    if (WORD_LIMITS[field]?.required && value.trim().length > 0 && isValid) {
      checkRequiredFieldsCompletion();
    }
  };

  const checkRequiredFieldsCompletion = () => {
    const requiredFields = ['researchTopic', 'abstract', 'objectives', 'methodology', 'resultsFindings', 'conclusionsRecommendations', 'references'];
    let allCompleted = true;
    
    for (const field of requiredFields) {
      if (!researchData[field] || researchData[field].trim().length === 0 || !isWordLimitValid(field, researchData[field])) {
        allCompleted = false;
        break;
      }
    }
    
    if (allCompleted && researchData.objectives.trim().length > 0) {
      setState(prev => ({
        ...prev,
        showSummaryPreviewTab: true,
        showSimilaritiesTab: true,
        showSubmitTab: true
      }));
      showToast("Success", "All required fields completed! You can now proceed to the next tabs.", "success");
    }
  };

  const setResearchAreaTextInput = (text) => {
    setState(prev => ({ ...prev, ResearchArea: text.target.value }));
  };

  const setResearchStatusTextInput = (text) => {
    setState(prev => ({ ...prev, ResearchStatusTextInput: text.target.value }));
  };

  const setResearchProjectTextInput = (text) => {
    setState(prev => ({ ...prev, ResearchProjectTextInput: text.target.value }));
  };

  const handlePdfFilePreview = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setState(prev => ({
        ...prev,
        pdfFilePreview: URL.createObjectURL(file)
      }));
    } else {
      alert('Please select a valid PDF file');
    }
  };

  const handleFile = (e) => {
    PdfTextExtractor(e);
    handlePdfFilePreview(e);

    let fileName = e.target.files[0].name;
    let fileSize = e.target.files[0].size;
    let file = e.target.files[0];

    setState(prev => ({
      ...prev,
      pdfFileName: file,
      fileName: fileName,
      fileSize: fileSize
    }));

    getBase64(file)
      .then(result => {
        const base64WithoutPrefix = result.substr('data:application/pdf;base64,'.length);
        setState(prev => ({
          ...prev,
          base64URL: base64WithoutPrefix
        }));
      })
      .catch(err => console.log(err));
  };

  const getBase64 = file => {
    return new Promise(resolve => {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });
  };

  const handlePreviewPdf = () => {
    if (state.pdfFilePreview) {
      setSelectedPdfUrl(state.pdfFilePreview);
      setPreviewPdfDialogOpen(true);
    }
  };

  const handleViewPdf = (fileName) => {
    const pdfUrl = `${BASE_URL}${fileName}`;
    setSelectedPdfUrl(pdfUrl);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const handleClosePreviewPdfDialog = () => {
    setPreviewPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const handleSubmit = async (e) => {
    setState(prev => ({ ...prev, showPostingAnimation: true }));

    const { userId, userUniversity, pdfFileName, fileSize } = state;

    // Validate required fields
    const requiredFields = ['researchTopic', 'abstract', 'objectives', 'methodology', 'resultsFindings', 'conclusionsRecommendations', 'references'];
    let hasError = false;
    
    for (const field of requiredFields) {
      if (!researchData[field] || researchData[field].trim().length === 0) {
        showToast("Required Field Missing", `${field.replace(/([A-Z])/g, ' $1').trim()} is required`, "warning");
        hasError = true;
      } else if (!isWordLimitValid(field, researchData[field])) {
        showToast("Word Limit Exceeded", `${field} exceeds the maximum ${WORD_LIMITS[field].max} words limit`, "warning");
        hasError = true;
      }
    }

    if (hasError) return;

    const formDataToSend = new FormData();
    formDataToSend.append('fileName', pdfFileName);
    formDataToSend.append('fileSize', fileSize);
    formDataToSend.append('researcherName', userId);
    formDataToSend.append('researchArea', state.ResearchArea);
    formDataToSend.append('researchStatus', state.ResearchStatusTextInput);
    formDataToSend.append('researchType', state.ResearchProjectTextInput);
    formDataToSend.append('universityName', userUniversity);
    formDataToSend.append('pdfPlainText', pdfPlainText);
    formDataToSend.append('researchTopic', researchData.researchTopic);
    formDataToSend.append('uploadDate', getTodaysDate());
    
    // Add all new fields
    formDataToSend.append('abstract', researchData.abstract);
    formDataToSend.append('acronyms', researchData.acronyms);
    formDataToSend.append('definition_of_terms', researchData.definitionOfTerms);
    formDataToSend.append('introduction', researchData.introduction);
    formDataToSend.append('objectives', researchData.objectives);
    formDataToSend.append('hypothesis', researchData.hypothesis);
    formDataToSend.append('justification', researchData.justification);
    formDataToSend.append('significance', researchData.significance);
    formDataToSend.append('theoretical_framework', researchData.theoreticalFramework);
    formDataToSend.append('literature_review', researchData.literatureReview);
    formDataToSend.append('methodology', researchData.methodology);
    formDataToSend.append('results_findings', researchData.resultsFindings);
    formDataToSend.append('discussion_of_results', researchData.discussionOfResults);
    formDataToSend.append('conclusions_recommendations', researchData.conclusionsRecommendations);
    formDataToSend.append('references', researchData.references);
    formDataToSend.append('appendices', researchData.appendices);

    if (postStatus === "UPDATE" && editingResearchId) {
      formDataToSend.append('updateId', editingResearchId);
    }

    try {
      let postRequest;
      if (postStatus === "ADD") {
        postRequest = await axios.post(APIResearchStore, formDataToSend, { headers });
      } else {
        postRequest = await axios.post(APIResearchUpdate, formDataToSend, { headers });
      }

      const response = postRequest.data;
      if (response.status === "success") {
        clearInputs();
        setState(prev => ({
          ...prev,
          showContinueToResearchBtn: true,
          showPostingAnimation: false,
          showSubmitBtn: false
        }));
        
        const successMessage = postStatus === "ADD" 
          ? "Research submitted successfully!" 
          : "Research updated successfully!";
          
        showToast("Success", successMessage, "success");
      } else {
        showToast("Error", "Unknown Server Error, Data Not Posted", "error");
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 422) {
          const errors = error.response.data.errors;
          let errorMessages = [];
          for (const field in errors) {
            errorMessages.push(...errors[field]);
          }
          showToast("Validation Error", errorMessages.join("\n"), "error");
        } else {
          showToast("Error", error.response.data.message || "An error occurred", "error");
        }
      } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
      } else {
        showToast("Error", "An unexpected error occurred", "error");
      }
    } finally {
      setState(prev => ({
        ...prev,
        showPostingAnimation: false
      }));
    }
  };

  const clearInputs = () => {
    setResearchData({
      researchTopic: '',
      abstract: '',
      acronyms: '',
      definitionOfTerms: '',
      introduction: '',
      objectives: '',
      hypothesis: '',
      justification: '',
      significance: '',
      theoreticalFramework: '',
      literatureReview: '',
      methodology: '',
      resultsFindings: '',
      discussionOfResults: '',
      conclusionsRecommendations: '',
      references: '',
      appendices: ''
    });
    setState(prev => ({
      ...prev,
      file: null,
      pdfPlainText: '',
      fileName: '',
      fileSize: '',
      ResearchArea: '',
      ResearchStatusTextInput: '',
      ResearchProjectTextInput: '',
    }));
    setWordCounts({});
  };

  // Professional Research Preview Component
  const ProfessionalResearchPreview = () => {
    const sections = [
      { key: 'researchTopic', label: 'Research Topic / Title', icon: <ArticleIcon />, required: true },
      { key: 'abstract', label: 'Abstract', icon: <Summarize />, required: true },
      { key: 'acronyms', label: 'Acronyms', icon: <FormatQuote />, required: false },
      { key: 'definitionOfTerms', label: 'Definition of Terms', icon: <MenuBook />, required: false },
      { key: 'introduction', label: 'Introduction', icon: <Description />, required: false },
      { key: 'objectives', label: 'Objectives', icon: <Assignment />, required: true },
      { key: 'hypothesis', label: 'Hypothesis', icon: <Science />, required: false },
      { key: 'theoreticalFramework', label: 'Theoretical Framework', icon: <AccountTree />, required: false },
      { key: 'literatureReview', label: 'Literature Review', icon: <LibraryBooks />, required: false },
      { key: 'methodology', label: 'Methodology', icon: <Tune />, required: true },
      { key: 'resultsFindings', label: 'Results / Findings', icon: <Calculate />, required: true },
      { key: 'discussionOfResults', label: 'Discussion of Results', icon: <Psychology />, required: false },
      { key: 'conclusionsRecommendations', label: 'Conclusions & Recommendations', icon: <CheckBox />, required: true },
      { key: 'references', label: 'References', icon: <LibraryBooks />, required: true },
      { key: 'appendices', label: 'Appendices', icon: <NoteAdd />, required: false }
    ];

    return (
      <StyledCard>
        <CardContent>
          <Box sx={{ 
            borderRadius: 2,
            p: 3,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`
          }}>
            {/* Research Header */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: 4,
              p: 3,
              backgroundColor: theme.palette.primary.main + '08',
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary.light}`
            }}>
              <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
                {researchData.researchTopic}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                by {state.userName}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mt: 2 }}>
                <Chip 
                  icon={<SchoolIcon />} 
                  label={`University: ${getIdCategory(state.userUniversity, universitiesData)}`} 
                  variant="outlined" 
                />
                <Chip 
                  icon={<ArticleIcon />} 
                  label={`Project: ${state.ResearchProjectTextInput}`} 
                  variant="outlined" 
                />
                <Chip 
                  icon={<CheckCircle />} 
                  label={`Status: ${state.ResearchStatusTextInput}`} 
                  variant="outlined" 
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* PDF Information with Preview Button */}
            <SectionCard>
              <SectionHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArticleIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="600">
                    PDF Document Information
                  </Typography>
                </Box>
                {state.pdfFilePreview && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Visibility />}
                    onClick={handlePreviewPdf}
                    size="small"
                  >
                    Preview PDF
                  </Button>
                )}
              </SectionHeader>
              <SectionContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" gutterBottom>
                      <strong>File Name:</strong> {state.fileName}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>File Size:</strong> {state.fileSize ? `${(state.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Text Extracted:</strong> {pdfPlainText ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>Similarity Check:</strong> {similarityResult ? 'Completed' : 'Pending'}
                    </Typography>
                  </Grid>
                </Grid>
              </SectionContent>
            </SectionCard>

            {/* Research Content Sections */}
            {sections.map((section) => {
              const content = researchData[section.key];
              if (!content || content.trim().length === 0) return null;
              
              return (
                <SectionCard key={section.key} sx={{ mt: 2 }}>
                  <SectionHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {section.icon}
                      <Typography variant="h6" color="primary" fontWeight="600">
                        {section.label}
                        {section.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                      </Typography>
                    </Box>
                    <WordCountChip 
                      label={`${wordCounts[section.key] || 0} / ${WORD_LIMITS[section.key]?.max || '∞'} words`}
                      isover={(wordCounts[section.key] > WORD_LIMITS[section.key]?.max).toString()}
                      size="small"
                    />
                  </SectionHeader>
                  <SectionContent>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {content}
                    </Typography>
                  </SectionContent>
                </SectionCard>
              );
            })}

            {/* Similarity Results */}
            {similarityResult && (
              <SectionCard sx={{ mt: 2 }}>
                <SectionHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Search color="primary" />
                    <Typography variant="h6" color="primary" fontWeight="600">
                      Similarity Analysis Results
                    </Typography>
                  </Box>
                </SectionHeader>
                <SectionContent>
                  <Alert 
                    severity={similarityResult.has_similarities ? 'warning' : 'success'}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body1">
                      {similarityResult.has_similarities 
                        ? `Found ${similarityResult.total_similar_documents} similar research documents (${similarityThreshold}% threshold)`
                        : `No significant similarities detected (${similarityThreshold}% threshold)`
                      }
                    </Typography>
                  </Alert>
                </SectionContent>
              </SectionCard>
            )}
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  // Render Research Overview Tab with all fields
  const renderResearchOverview = () => {
    const overviewFields = [
      { key: 'researchTopic', label: 'Research Topic / Title', placeholder: 'Enter your research topic or title here...', required: true, maxWords: 20, icon: <ArticleIcon /> },
      { key: 'abstract', label: 'Abstract', placeholder: 'Provide a brief summary of your research...', required: true, maxWords: 300, icon: <Summarize />, multiline: true, rows: 4 },
      { key: 'acronyms', label: 'Acronyms', placeholder: 'List any acronyms used in your research...', required: false, maxWords: 1000, icon: <FormatQuote />, multiline: true, rows: 2 },
      { key: 'definitionOfTerms', label: 'Definition of Terms', placeholder: 'Define key terms used in your research...', required: false, maxWords: 200, icon: <MenuBook />, multiline: true, rows: 3 },
      { key: 'introduction', label: 'Introduction', placeholder: 'Introduce your research topic and background...', required: false, maxWords: 500, icon: <Description />, multiline: true, rows: 4 },
      { key: 'objectives', label: 'Objectives', placeholder: 'State the objectives of your research...', required: true, maxWords: 100, icon: <Assignment />, multiline: true, rows: 2 },
      { key: 'hypothesis', label: 'Hypothesis', placeholder: 'State your research hypothesis...', required: false, maxWords: 100, icon: <Science />, multiline: true, rows: 2 },
      { key: 'justification', label: 'Justification (Internal)', placeholder: 'Justify your research...', required: false, maxWords: 1000, icon: <Info />, multiline: true, rows: 3, hiddenInView: true },
      { key: 'significance', label: 'Significance (Internal)', placeholder: 'Explain the significance of your research...', required: false, maxWords: 1000, icon: <Info />, multiline: true, rows: 3, hiddenInView: true },
      { key: 'theoreticalFramework', label: 'Theoretical Framework', placeholder: 'Describe the theoretical framework...', required: false, maxWords: 500, icon: <AccountTree />, multiline: true, rows: 4 },
      { key: 'literatureReview', label: 'Literature Review', placeholder: 'Review relevant literature...', required: false, maxWords: 2000, icon: <LibraryBooks />, multiline: true, rows: 6 },
      { key: 'methodology', label: 'Methodology', placeholder: 'Describe your research methodology...', required: true, maxWords: 5000, icon: <Tune />, multiline: true, rows: 6 },
      { key: 'resultsFindings', label: 'Results / Findings', placeholder: 'Present your research results and findings...', required: true, maxWords: 5000, icon: <Calculate />, multiline: true, rows: 6 },
      { key: 'discussionOfResults', label: 'Discussion of Results', placeholder: 'Discuss your research results...', required: false, maxWords: 2000, icon: <Psychology />, multiline: true, rows: 5 },
      { key: 'conclusionsRecommendations', label: 'Conclusions & Recommendations', placeholder: 'Present your conclusions and recommendations...', required: true, maxWords: 1000, icon: <CheckBox />, multiline: true, rows: 4 },
      { key: 'references', label: 'References', placeholder: 'List your references...', required: true, maxWords: 1000, icon: <LibraryBooks />, multiline: true, rows: 4 },
      { key: 'appendices', label: 'Appendices', placeholder: 'Include any appendices...', required: false, maxWords: 1000, icon: <NoteAdd />, multiline: true, rows: 3 }
    ];

    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Research Submission Guidelines:</strong> Please fill out all required fields (*). 
            Each field has a maximum word limit as indicated. Fields marked with "Internal" are for administrative use only 
            and will not appear in the public view of your research.
          </Typography>
        </Alert>

        {overviewFields.map((field) => (
          <StyledAccordion key={field.key} defaultExpanded={field.required}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {field.icon}
                  <Typography variant="subtitle1">
                    {field.label}
                    {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                    {field.hiddenInView && <Chip label="Internal Only" size="small" color="info" sx={{ ml: 1 }} />}
                  </Typography>
                </Box>
                <WordCountChip 
                  label={`${wordCounts[field.key] || 0} / ${field.maxWords} words`}
                  isover={(wordCounts[field.key] > field.maxWords).toString()}
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline={field.multiline || false}
                rows={field.rows || 1}
                label={field.label}
                variant="outlined"
                value={researchData[field.key] || ''}
                onChange={(e) => handleResearchFieldChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                error={wordCounts[field.key] > field.maxWords}
                helperText={wordCounts[field.key] > field.maxWords ? `Word limit exceeded! Maximum ${field.maxWords} words allowed.` : `${wordCounts[field.key] || 0} words used`}
                sx={{ mb: 1 }}
              />
            </AccordionDetails>
          </StyledAccordion>
        ))}

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> After completing the required fields, upload your PDF file in the Research Status tab to extract text and check for similarities (15% threshold).
          </Typography>
        </Alert>
      </Box>
    );
  };

  // Render Research Status Tab with previous attributes
  const renderResearchStatus = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <ArticleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              PDF Attachment
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                Attach your research PDF file
              </Typography>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFile}
                style={{ display: 'none' }}
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload">
                <Button 
                  variant="contained" 
                  component="span"
                  color="primary"
                  disabled={extractingSections}
                  startIcon={extractingSections ? <CircularProgress size={20} /> : <ArticleIcon />}
                >
                  {extractingSections ? 'Processing PDF...' : 'Upload PDF'}
                </Button>
              </label>
              {state.fileName && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {state.fileName}
                </Typography>
              )}
              {state.pdfPlainTextError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {state.pdfPlainTextError}
                </Typography>
              )}
              {state.pdfPlainTextSuccess && (
                <Typography color="primary" variant="body2" sx={{ mt: 1 }}>
                  {state.pdfPlainTextSuccess}
                </Typography>
              )}
              {extractingSections && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary">
                    Extracting text from PDF...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="body1" gutterBottom>
                Research Area
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={state.ResearchArea}
                onChange={setResearchAreaTextInput}
                SelectProps={{ native: true }}
              >
                <option value="">-- Select Research Area --</option>
                {state.ThemesData.map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </TextField>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              Research Status
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                Research Status
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={state.ResearchStatusTextInput}
                onChange={setResearchStatusTextInput}
                SelectProps={{ native: true }}
              >
                <option value="">-- Select Status --</option>
                <option value="Completed">Completed</option>
                <option value="Work In Progress">Work In Progress</option>
              </TextField>
            </Box>

            <Box>
              <Typography variant="body1" gutterBottom>
                Research Project Type
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={state.ResearchProjectTextInput}
                onChange={setResearchProjectTextInput}
                SelectProps={{ native: true }}
              >
                <option value="">-- Select Project Type --</option>
                <option value="Thesis">Thesis - PhD</option>
                <option value="Journal">Journal - Others</option>
                <option value="Dissertation">Dissertation - Master</option>
              </TextField>
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>
    </Grid>
  );

  // Similarity check function
  const handleSubmitSimilarity = async (pdfPlainText, excludeResearchId = null) => {
    if (!pdfPlainText || pdfPlainText.trim().length === 0) {
      showToast("Error", "Please upload and process PDF first", "error");
      return;
    }

    if (pdfPlainText.trim().length < 50) {
      showToast("Error", "Text is too short for similarity analysis", "error");
      return;
    }

    setCheckingSimilarities(true);
    try {
      const payload = {
        pdfPlainText: pdfPlainText.trim(),
        threshold: similarityThreshold
      };

      if (excludeResearchId) {
        payload.exclude_research_id = excludeResearchId;
      }

      const response = await axios.post(APIResearchCheckSimilarities, payload, { headers });
      
      if (response.data.status === 'success') {
        const similarityData = response.data.data;
        setSimilarityResult(similarityData);
        
        if (similarityData.has_similarities) {
          showToast(
            "Similarities Found",
            `Found ${similarityData.total_similar_documents} similar research documents (${similarityThreshold}% threshold)`,
            "warning"
          );
        } else {
          showToast(
            "No Similarities Found",
            `No significant similarities detected with existing research (${similarityThreshold}% threshold)`,
            "success"
          );
        }
      } else {
        throw new Error(response.data.message || 'Unknown error occurred');
      }
    } catch (error) {
      let errorMessage = "Similarity check failed";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showToast("Similarity Check Failed", errorMessage, "error");
    } finally {
      setCheckingSimilarities(false);
    }
  };

  const getSimilarityLevel = (percentage) => {
    if (percentage >= 90) return 'HIGH_RISK';
    if (percentage >= 80) return 'MEDIUM_RISK';
    if (percentage >= similarityThreshold) return 'LOW_RISK';
    return 'NO_RISK';
  };

  const renderSimilarityItem = (similar, index) => (
    <ListItem key={index} divider sx={{ py: 2 }}>
      <ListItemIcon>
        <Warning color={
          similar.status === 'HIGH_RISK' ? 'error' : 
          similar.status === 'MEDIUM_RISK' ? 'warning' : 'info'
        } />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="subtitle1" fontWeight="medium">
            {similar.research_topic}
          </Typography>
        }
        secondary={
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Person fontSize="small" color="primary" />
                  <Typography variant="body2">
                    Researcher: {getIdFullName(similar.researcher_name, researchersData)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SchoolIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    University: {getIdCategory(similar.university_name, universitiesData)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ArticleIcon fontSize="small" color="primary" />
                  <Typography variant="body2">
                    Uploaded: {similar.upload_date}
                  </Typography>
                </Box>
                {similar.file_name && (
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleViewPdf(similar.file_name)}
                    sx={{ textTransform: 'none' }}
                  >
                    View PDF
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        }
      />
      <Box sx={{ textAlign: 'right', minWidth: 120 }}>
        <Chip
          label={`${similar.similarity_percentage}%`}
          color={
            similar.status === 'HIGH_RISK' ? 'error' :
            similar.status === 'MEDIUM_RISK' ? 'warning' : 'info'
          }
          variant="filled"
          size="medium"
        />
        <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 'medium' }}>
          <Chip
            icon={<SchoolIcon />}
            label={similar.status.replace('_', ' ')}
            color={
              similar.status === 'HIGH_RISK' ? 'error' :
              similar.status === 'MEDIUM_RISK' ? 'warning' : 'info'
            }
            size="small"
            variant="outlined"
          />
        </Typography>
      </Box>
    </ListItem>
  );

  if (error) return showToast("Error", error, "error");
  if (loading) {
    return (
      <>
        <NavigationMenu />
        <LoadingState />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <NavigationMenu />
      <Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
          <Box sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: postStatus === 'UPDATE' ? theme.palette.warning.main : theme.palette.success.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
                {postStatus === 'UPDATE' ? 'Edit Research' : 'Research Uploads'}
              </Typography>
              <Typography variant="subtitle1">
                <strong>{postStatus === 'UPDATE' ? 'Update Your Research Details' : 'Submit Your Research Today!'}</strong>
              </Typography>
              {postStatus === 'UPDATE' && (
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  Editing Research ID: {editingResearchId || 'Not specified'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component={Link}
                to="/researches"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<ArticleIcon />}
                sx={{
                  fontWeight: 'bold',
                  boxShadow: 3,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Research Uploads
              </Button>
              {postStatus === 'UPDATE' && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  startIcon={<Edit />}
                  sx={{
                    fontWeight: 'bold',
                    borderColor: 'white',
                    background: "#4caf50",
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white'
                    }
                  }}
                >
                  Edit Mode
                </Button>
              )}
            </Box>
          </Box>
        </Slide>
        
        <StyledCard>
          <CardContent>
            <Box sx={{ width: '100%' }}>
              <Tabs
                value={state.activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.palette.primary.main,
                    height: 3,
                  },
                }}
              >
                <CustomTab
                  label="Research Overview"
                  value="ResearchHit"
                  icon={getTabIcon('ResearchHit')}
                  iconPosition="start"
                  iscompleted={isTabCompleted('ResearchHit').toString()}
                />
                <CustomTab
                  label="Research Status"
                  value="Attachments"
                  icon={getTabIcon('Attachments')}
                  iconPosition="start"
                  iscompleted={isTabCompleted('Attachments').toString()}
                  isdisabled={(!getTabEnabled('Attachments')).toString()}
                />
                <CustomTab
                  label="Summary Preview"
                  value="Summary-Preview"
                  icon={getTabIcon('Summary-Preview')}
                  iconPosition="start"
                  iscompleted={isTabCompleted('Summary-Preview').toString()}
                  isdisabled={(!getTabEnabled('Summary-Preview')).toString()}
                />
                <CustomTab
                  label="Similarities"
                  value="Similarities"
                  icon={getTabIcon('Similarities')}
                  iconPosition="start"
                  iscompleted={isTabCompleted('Similarities').toString()}
                  isdisabled={(!getTabEnabled('Similarities')).toString()}
                />
                <CustomTab
                  label="Submit"
                  value="Submit"
                  icon={getTabIcon('Submit')}
                  iconPosition="start"
                  iscompleted={isTabCompleted('Submit').toString()}
                  isdisabled={(!getTabEnabled('Submit')).toString()}
                />
              </Tabs>
            </Box>

            <Box sx={{ pt: 3 }}>
              {state.activeTab === 'ResearchHit' && renderResearchOverview()}

              {state.activeTab === 'Attachments' && (
                state.showResearchStatusTab ? (
                  renderResearchStatus()
                ) : (
                  <Box textAlign="center" py={5}>
                    <Error color="error" fontSize="large" />
                    <Typography variant="h6" color="error" gutterBottom>
                      Research Topic Required
                    </Typography>
                    <Typography variant="body1">
                      Please enter a Research Topic in the Research Overview tab first
                    </Typography>
                  </Box>
                )
              )}

              {state.activeTab === 'Summary-Preview' && (
                state.showSummaryPreviewTab ? (
                  <ProfessionalResearchPreview />
                ) : (
                  <Box textAlign="center" py={5}>
                    <Error color="error" fontSize="large" />
                    <Typography variant="h6" color="error" gutterBottom>
                      Complete Required Fields First
                    </Typography>
                    <Typography variant="body1">
                      Please fill in all required fields in the previous tabs
                    </Typography>
                  </Box>
                )
              )}

              {state.activeTab === 'Similarities' && (
                state.showSimilaritiesTab ? (
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledCard>
                          <CardContent sx={{ minWidth: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                              <Box>
                                <Typography variant="h5" color="primary" gutterBottom>
                                  Research Similarity Analysis
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Compare your research with existing documents to ensure originality (15% threshold)
                                </Typography>
                                {postStatus === 'UPDATE' && (
                                  <Chip
                                    label="Current research excluded from comparison"
                                    color="info"
                                    size="small"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Box>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSubmitSimilarity(pdfPlainText, editingResearchId)}
                                disabled={checkingSimilarities || !pdfPlainText}
                                startIcon={checkingSimilarities ? <CircularProgress size={20} /> : <Search />}
                                size="large"
                              >
                                {checkingSimilarities ? 'Analyzing...' : 'Check Similarities'}
                              </Button>
                            </Box>

                            {checkingSimilarities && (
                              <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={60} />
                                <Typography variant="h6" sx={{ mt: 2 }}>
                                  Analyzing Research Content
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Comparing your document with existing research (15% threshold)...
                                </Typography>
                              </Box>
                            )}

                            {similarityResult && !checkingSimilarities && (
                              <Box>
                                <Alert
                                  severity={similarityResult.has_similarities ? 'warning' : 'success'}
                                  sx={{ mb: 3 }}
                                  icon={similarityResult.has_similarities ? <Warning /> : <CheckCircle />}
                                >
                                  <Typography variant="h6">
                                    {similarityResult.has_similarities
                                      ? `Similar Documents Found`
                                      : 'No Significant Similarities'
                                    }
                                  </Typography>
                                  <Typography variant="body2">
                                    {similarityResult.has_similarities
                                      ? `Found ${similarityResult.total_similar_documents} research document(s) with similarity above ${similarityThreshold}%`
                                      : `Your research appears to be original and unique (${similarityThreshold}% threshold)`
                                    }
                                  </Typography>
                                </Alert>

                                {similarityResult.has_similarities && (
                                  <Box>
                                    <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                                      Similar Research Documents ({similarityResult.similarities.length})
                                    </Typography>
                                    <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                                      {similarityResult.similarities.map(renderSimilarityItem)}
                                    </List>

                                    <Paper sx={{ p: 3, mt: 3, backgroundColor: '#979797' }}>
                                      <Typography variant="h6" gutterBottom color="white">
                                        <b>Understanding Similarity Levels (15% Threshold)</b>
                                      </Typography>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', p: 2, color: 'white', bgcolor: '#c91940', borderRadius: 1 }}>
                                            <Box sx={{ width: 16, height: 16, backgroundColor: 'white', borderRadius: '50%', mr: 2 }} />
                                            <Box>
                                              <Typography variant="subtitle2" fontWeight="bold">
                                                HIGH RISK (≥90%)
                                              </Typography>
                                              <Typography variant="caption">
                                                Potential plagiarism - Requires immediate attention
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', p: 2, color: 'white', bgcolor: '#ffb63a', borderRadius: 1 }}>
                                            <Box sx={{ width: 16, height: 16, backgroundColor: 'white', borderRadius: '50%', mr: 2 }} />
                                            <Box>
                                              <Typography variant="subtitle2" fontWeight="bold">
                                                MEDIUM RISK (80-89%)
                                              </Typography>
                                              <Typography variant="caption">
                                                Significant overlap - Needs review and citations
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', p: 2, color: 'white', bgcolor: '#074900', borderRadius: 1 }}>
                                            <Box sx={{ width: 16, height: 16, backgroundColor: 'white', borderRadius: '50%', mr: 2 }} />
                                            <Box>
                                              <Typography variant="subtitle2" fontWeight="bold">
                                                LOW RISK (15-79%)
                                              </Typography>
                                              <Typography variant="caption">
                                                Similarities detected - Ensure proper citations (15% threshold)
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Paper>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {!similarityResult && !checkingSimilarities && (
                              <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Search sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  Ready for Similarity Check
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                  Click the "Check Similarities" button to analyze your research against existing documents in our database (15% threshold).
                                </Typography>
                                <Chip
                                  icon={<Info />}
                                  label={`Similarity Threshold: ${similarityThreshold}%`}
                                  variant="outlined"
                                  color="primary"
                                />
                              </Box>
                            )}
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Box textAlign="center" py={5}>
                    <Error color="error" fontSize="large" />
                    <Typography variant="h6" color="error" gutterBottom>
                      Complete Required Fields First
                    </Typography>
                    <Typography variant="body1">
                      Please fill in all required fields in the previous tabs
                    </Typography>
                  </Box>
                )
              )}

              {state.activeTab === 'Submit' && (
                state.showSubmitTab ? (
                  state.userLoggedIn ? (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <StyledCard>
                          <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                              Research Progress
                            </Typography>
                            <Typography color="primary" gutterBottom>
                              Completed
                            </Typography>
                            <LinearProgress 
                              variant="determinate" 
                              value={100} 
                              color="primary"
                              sx={{ height: 10, borderRadius: 5 }}
                            />
                            <Typography variant="body2" textAlign="right" mt={1}>
                              {state.completedValue}
                            </Typography>
                          </CardContent>
                        </StyledCard>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <StyledCard>
                          <CardContent>
                            <Typography variant="h6" color="success.main" gutterBottom>
                              {postStatus === 'UPDATE' ? 'Update Your Research' : 'Submit Your Research'}
                            </Typography>
                            <Typography color="success.main" gutterBottom>
                              {postStatus === 'UPDATE' ? 'Update Your Research' : 'Submit Your Research'}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                              {state.showSubmitBtn && (
                                <Button
                                  variant="contained"
                                  color={postStatus === 'UPDATE' ? 'warning' : 'success'}
                                  size="large"
                                  onClick={handleSubmit}
                                  disabled={state.showPostingAnimation}
                                  startIcon={postStatus === 'UPDATE' ? <Edit /> : <ArticleIcon />}
                                >
                                  {postStatus === 'UPDATE' ? 'Update Research' : 'Submit Research'}
                                </Button>
                              )}

                              {state.showContinueToResearchBtn && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="large"
                                  onClick={() => navigate("/researches")}
                                >
                                  Continue To Research Uploads
                                </Button>
                              )}
                            </Box>

                            {state.showPostingAnimation && (
                              <Box textAlign="center" py={2}>
                                <CircularProgress color="error" />
                                <Typography color="error" sx={{ mt: 1 }}>
                                  <strong>{postStatus === 'UPDATE' ? '... Updating ...' : '... Posting ...'}</strong>
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </StyledCard>
                      </Grid>
                    </Grid>
                  ) : (
                    <GuestUser />
                  )
                ) : (
                  <Box textAlign="center" py={5}>
                    <Error color="error" fontSize="large" />
                    <Typography variant="h6" color="error" gutterBottom>
                      Complete Required Fields First
                    </Typography>
                    <Typography variant="body1">
                      Please fill in all required fields in the previous tabs
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Container>

      {/* PDF Viewer for Uploaded PDF Preview */}
      <PDFViewer
        open={previewPdfDialogOpen}
        onClose={handleClosePreviewPdfDialog}
        pdfUrl={selectedPdfUrl}
        title="Uploaded Research PDF Preview"
      />

      {/* PDF Viewer for Similar Research Documents */}
      <PDFViewer
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        pdfUrl={selectedPdfUrl}
        title="Similar Research Document"
      />
    </Box>
  );
};

export default SubmitResearch;

const useStyles = makeStyles((theme) => ({
  formInput: {
    marginBottom: 10,
  },
}));